import type { FileItem, FileStats, Folder } from '@erp/shared';
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  FileType,
  Files as FilesIcon,
  Folder as FolderIcon,
  FolderInput,
  FolderOpen,
  FolderPlus,
  HardDrive,
  Image as ImageIcon,
  Inbox,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Move,
  Palette,
  Pencil,
  Receipt,
  Search,
  Store,
  Tags,
  Trash2,
  Upload,
  UploadCloud,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Badge, Button, Card, confirmDialog, EmptyState, Input, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { cn } from '../../lib/cn.js';
import {
  ATTACHMENT_ALLOWED_MIME,
  ATTACHMENT_MAX_BYTES,
  createFolder,
  deleteAttachment,
  deleteFolder,
  fetchAttachmentObjectUrl,
  getFileStats,
  isImageMime,
  type ListFilesParams,
  listFiles,
  listFolders,
  moveFile,
  updateFolder,
  uploadToFolder,
} from '../../lib/files.js';
import { formatDate } from '../../lib/intl.js';

/** Smart views — virtual folders that group files by their source entity_type. */
interface SmartView {
  key: string;
  icon: typeof FilesIcon;
  types: string[] | null; // null = all; matched against entity_type
  unfiled?: boolean;
  /** Maps to a `seg-*` accent color (shared with the storage meter). */
  tone?: string;
}
const SMART_VIEWS: SmartView[] = [
  { key: 'all', icon: FilesIcon, types: null },
  { key: 'unfiled', icon: Inbox, types: null, unfiled: true },
  { key: 'images', icon: ImageIcon, types: ['item'], tone: 'images' },
  { key: 'categories', icon: Tags, types: ['category'], tone: 'categories' },
  { key: 'branding', icon: Palette, types: ['cms_hero', 'cms_settings'], tone: 'branding' },
  { key: 'pages', icon: Store, types: ['cms_page'], tone: 'pages' },
  { key: 'documents', icon: Receipt, types: ['invoice', 'bill', 'sales_order', 'purchase_order'], tone: 'documents' },
  { key: 'uploads', icon: UploadCloud, types: ['file'], tone: 'uploads' },
];

type View =
  | { kind: 'smart'; key: string }
  | { kind: 'folder'; id: string };

/** Files fetched per request — one screenful and a bit, tapped again on demand. */
const PAGE_SIZE = 60;
/** Mirror of the server's page ceiling, for the "keep what is on screen" reload. */
const MAX_LOADED = 500;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Icon + color class for a non-image file, by MIME family. */
function docMeta(mime: string): { Icon: typeof FileText; cls: string } {
  if (mime === 'application/pdf') return { Icon: FileType, cls: 'doc-pdf' };
  if (mime.includes('spreadsheet') || mime === 'text/csv' || mime === 'application/vnd.ms-excel') {
    return { Icon: FileSpreadsheet, cls: 'doc-sheet' };
  }
  if (mime.includes('word') || mime === 'application/msword') return { Icon: FileText, cls: 'doc-word' };
  return { Icon: FileText, cls: 'doc-generic' };
}

function indexFolders(folders: Folder[]): Map<string | null, Folder[]> {
  const byParent = new Map<string | null, Folder[]>();
  for (const f of folders) {
    const key = f.parentId ?? null;
    const list = byParent.get(key);
    if (list) list.push(f);
    else byParent.set(key, [f]);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  return byParent;
}

interface FolderRow {
  folder: Folder;
  depth: number;
  hasChildren: boolean;
}

function subtreeMatches(f: Folder, byParent: Map<string | null, Folder[]>, q: string): boolean {
  if (f.name.toLowerCase().includes(q)) return true;
  return (byParent.get(f.id) ?? []).some((k) => subtreeMatches(k, byParent, q));
}

function flattenFolders(
  byParent: Map<string | null, Folder[]>,
  query: string,
  collapsed: ReadonlySet<string>,
): FolderRow[] {
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const out: FolderRow[] = [];
  const walk = (f: Folder, depth: number): void => {
    if (searching && !subtreeMatches(f, byParent, q)) return;
    const kids = byParent.get(f.id) ?? [];
    out.push({ folder: f, depth, hasChildren: kids.length > 0 });
    if (kids.length > 0 && !(collapsed.has(f.id) && !searching)) {
      for (const k of kids) walk(k, depth + 1);
    }
  };
  for (const root of byParent.get(null) ?? []) walk(root, 0);
  return out;
}

/** Path from root → folder, for the breadcrumb and indented selects. */
function folderPath(byId: Map<string, Folder>, id: string): Folder[] {
  const path: Folder[] = [];
  const seen = new Set<string>();
  let cur: Folder | undefined = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    path.unshift(cur);
    seen.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return path;
}

export function FileManagerPage(): JSX.Element {
  const { t } = useTranslation('files');
  const { t: tc } = useTranslation();
  const { i18n } = useTranslation();
  const canWrite = usePermission('attachments.write');
  const canDelete = usePermission('attachments.delete');

  const [folders, setFolders] = useState<Folder[] | null>(null);
  const [files, setFiles] = useState<FileItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<FileStats | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [view, setView] = useState<View>({ kind: 'smart', key: 'all' });
  const [query, setQuery] = useState('');
  /** `query` typed; `search` is what the server has been asked (debounced). */
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [folderDialog, setFolderDialog] = useState<{ mode: 'create' | 'rename'; parentId: string | null; folder?: Folder } | null>(null);
  const [moveTarget, setMoveTarget] = useState<FileItem | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);

  const reloadFolders = useCallback(async (): Promise<void> => {
    setFolders(await listFolders());
  }, []);
  const reloadStats = useCallback(async (): Promise<void> => {
    setStats(await getFileStats());
  }, []);
  useEffect(() => {
    void reloadFolders();
    void reloadStats();
  }, [reloadFolders, reloadStats]);

  // Typing narrows the folder tree instantly; the server is asked once you stop.
  useEffect(() => {
    const h = window.setTimeout(() => setSearch(query), 300);
    return () => window.clearTimeout(h);
  }, [query]);

  /**
   * The current view expressed as request parameters. Filtering moved to the
   * server so a page of files is genuinely a page: the browser no longer holds
   * the whole catalogue in order to hide most of it.
   */
  const listParams = useMemo((): ListFilesParams => {
    const p: ListFilesParams = {};
    if (view.kind === 'folder') p.folderId = view.id;
    else {
      const sv = SMART_VIEWS.find((s) => s.key === view.key);
      p.folderId = sv?.unfiled ? 'unfiled' : 'all';
      if (sv?.types) p.entityTypes = sv.types;
    }
    if (search.trim()) p.q = search.trim();
    return p;
  }, [view, search]);

  // Bumped on every fresh request, so a slow answer that a newer view has
  // already superseded is thrown away instead of overwriting it.
  const reqRef = useRef(0);
  const loadedRef = useRef(0);
  loadedRef.current = files?.length ?? 0;

  useEffect(() => {
    const seq = ++reqRef.current;
    setFiles(null);
    void listFiles({ ...listParams, limit: PAGE_SIZE, offset: 0 })
      .then((r) => {
        if (reqRef.current !== seq) return;
        setFiles(r.items);
        setTotal(r.total);
      })
      .catch(() => {
        if (reqRef.current !== seq) return;
        setFiles([]);
        setTotal(0);
      });
  }, [listParams]);

  const loadMore = useCallback(async (): Promise<void> => {
    const seq = reqRef.current;
    setLoadingMore(true);
    try {
      const r = await listFiles({ ...listParams, limit: PAGE_SIZE, offset: loadedRef.current });
      if (reqRef.current !== seq) return; // the view changed while we waited
      setFiles((prev) => {
        const have = new Set((prev ?? []).map((f) => f.id));
        return [...(prev ?? []), ...r.items.filter((f) => !have.has(f.id))];
      });
      setTotal(r.total);
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setLoadingMore(false);
    }
  }, [listParams, t]);

  /** After an upload/move/delete: re-read what is on screen, and the counters. */
  const reloadFiles = useCallback(async (): Promise<void> => {
    const seq = ++reqRef.current;
    const keep = Math.min(Math.max(PAGE_SIZE, loadedRef.current), MAX_LOADED);
    const [list] = await Promise.all([
      listFiles({ ...listParams, limit: keep, offset: 0 }),
      reloadStats(),
    ]);
    if (reqRef.current !== seq) return;
    setFiles(list.items);
    setTotal(list.total);
  }, [listParams, reloadStats]);

  const byParent = useMemo(() => indexFolders(folders ?? []), [folders]);
  const byId = useMemo(() => new Map((folders ?? []).map((f) => [f.id, f] as const)), [folders]);
  const folderRows = useMemo(() => flattenFolders(byParent, query, collapsed), [byParent, query, collapsed]);

  // Per-view counts, from the tenant-wide aggregate (a page cannot answer this).
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    if (!stats) return m;
    for (const sv of SMART_VIEWS) {
      m[`smart:${sv.key}`] = sv.unfiled
        ? stats.unfiledCount
        : sv.types
          ? sv.types.reduce((a, type) => a + (stats.bySource[type]?.count ?? 0), 0)
          : stats.totalCount;
    }
    for (const [id, n] of Object.entries(stats.byFolder)) m[`folder:${id}`] = n;
    return m;
  }, [stats]);

  // Storage usage — total bytes + a breakdown by source category (for the meter).
  const storage = useMemo(() => {
    if (!stats) return { total: 0, count: 0, buckets: [] as Array<{ key: string; bytes: number }> };
    const typed = SMART_VIEWS.filter((s) => s.types);
    const known = new Set(typed.flatMap((s) => s.types ?? []));
    const buckets = typed
      .map((s) => ({ key: s.key, bytes: (s.types ?? []).reduce((a, type) => a + (stats.bySource[type]?.bytes ?? 0), 0) }))
      .filter((b) => b.bytes > 0);
    const other = Object.entries(stats.bySource)
      .filter(([type]) => !known.has(type))
      .reduce((a, [, v]) => a + v.bytes, 0);
    if (other > 0) buckets.push({ key: 'other', bytes: other });
    buckets.sort((a, b) => b.bytes - a.bytes);
    return { total: stats.totalBytes, count: stats.totalCount, buckets };
  }, [stats]);

  // The server already applied the view + the search; this is simply what we hold.
  const visibleFiles = files ?? [];

  const toggle = (id: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const currentFolderId = view.kind === 'folder' ? view.id : null;
  const heading =
    view.kind === 'folder'
      ? byId.get(view.id)?.name ?? t('title')
      : t(`views.${view.key}`, { defaultValue: view.key });

  // ---- uploads ----
  const validate = useCallback(
    (file: File): string | null => {
      if (!file.type || !ATTACHMENT_ALLOWED_MIME.has(file.type)) return t('errors.mime');
      if (file.size > ATTACHMENT_MAX_BYTES) return t('errors.size');
      return null;
    },
    [t],
  );
  const doUpload = useCallback(
    async (list: FileList | File[]): Promise<void> => {
      const arr = Array.from(list);
      const bad = arr.map((f) => ({ f, e: validate(f) })).find((x) => x.e);
      if (bad) toast.error(`${bad.f.name}: ${bad.e}`);
      const valid = arr.filter((f) => !validate(f));
      if (valid.length === 0) return;
      setBusy(true);
      try {
        for (const f of valid) await uploadToFolder(f, currentFolderId);
        await reloadFiles();
        toast.success(t('upload'));
      } catch {
        toast.error(t('errors.generic'));
      } finally {
        setBusy(false);
      }
    },
    [validate, currentFolderId, reloadFiles, t],
  );

  // ---- file actions ----
  const onMove = async (folderId: string | null): Promise<void> => {
    if (!moveTarget) return;
    try {
      await moveFile(moveTarget.id, folderId);
      setMoveTarget(null);
      await reloadFiles();
    } catch {
      toast.error(t('errors.generic'));
    }
  };
  const onDeleteFile = async (f: FileItem): Promise<void> => {
    const linked = f.entityType !== 'file';
    const message = linked
      ? `${t('deleteFileConfirm', { name: f.filename })}\n\n${t('deleteLinkedWarning', { source: t(`source.${f.entityType}`, { defaultValue: f.entityType }) })}`
      : t('deleteFileConfirm', { name: f.filename });
    if (!(await confirmDialog({ message, tone: 'danger' }))) return;
    try {
      await deleteAttachment(f.id);
      if (previewFile?.id === f.id) setPreviewFile(null);
      await reloadFiles();
    } catch {
      toast.error(t('errors.generic'));
    }
  };

  // ---- folder actions ----
  const onSaveFolder = async (name: string): Promise<void> => {
    if (!folderDialog) return;
    try {
      if (folderDialog.mode === 'create') await createFolder({ name, parentId: folderDialog.parentId ?? undefined });
      else if (folderDialog.folder) await updateFolder(folderDialog.folder.id, { name });
      setFolderDialog(null);
      await reloadFolders();
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? t('errors.nameTaken') : t('errors.generic'));
    }
  };
  const onDeleteFolder = async (f: Folder): Promise<void> => {
    if (!(await confirmDialog({ message: t('deleteFolderConfirm', { name: f.name }), tone: 'danger' }))) return;
    try {
      await deleteFolder(f.id);
      if (view.kind === 'folder' && view.id === f.id) setView({ kind: 'smart', key: 'all' });
      await Promise.all([reloadFolders(), reloadFiles()]);
    } catch {
      toast.error(t('errors.generic'));
    }
  };

  const onDownload = useCallback(async (f: FileItem): Promise<void> => {
    const url = await fetchAttachmentObjectUrl(f.id);
    const link = document.createElement('a');
    link.href = url;
    link.download = f.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, []);

  const loading = folders === null || files === null;

  return (
    <div className="fm-view flex flex-col gap-4">
      <style>{FM_CSS}</style>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('subtitle')}</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setFolderDialog({ mode: 'create', parentId: currentFolderId })}>
              <FolderPlus className="h-4 w-4" aria-hidden />
              {currentFolderId ? t('newSubfolder') : t('newFolder')}
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} loading={busy}>
              <Upload className="h-4 w-4" aria-hidden />
              {t('upload')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={[...ATTACHMENT_ALLOWED_MIME].join(',')}
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void doUpload(e.target.files);
                if (e.target) e.target.value = '';
              }}
            />
          </div>
        )}
      </header>

      <div className="fm-layout">
        {/* ---- sidebar: smart views + folder tree ---- */}
        <Card className="fm-side p-0">
          <div className="fm-side-scroll">
            <div className="fm-cap">{t('smartViews')}</div>
            <nav className="fm-nav">
              {SMART_VIEWS.map((sv) => {
                const Icon = sv.icon;
                const active = view.kind === 'smart' && view.key === sv.key;
                return (
                  <button key={sv.key} type="button" className={cn('fm-nav-item', active && 'active')} onClick={() => setView({ kind: 'smart', key: sv.key })}>
                    <span className={cn('fm-svico', sv.tone && `tone-${sv.tone}`)}>
                      <Icon className="h-[18px] w-[18px]" aria-hidden />
                    </span>
                    <span className="truncate">{t(`views.${sv.key}`)}</span>
                    <span className="fm-count">{counts[`smart:${sv.key}`] ?? 0}</span>
                  </button>
                );
              })}
            </nav>

            <div className="fm-cap fm-cap-folders">
              <span>{t('folders')}</span>
              {canWrite && (
                <button type="button" className="fm-cap-add" onClick={() => setFolderDialog({ mode: 'create', parentId: null })} title={t('newFolder')} aria-label={t('newFolder')}>
                  <FolderPlus className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
            {loading ? (
              <div className="px-3 py-2"><Skeleton className="h-5 w-full" /></div>
            ) : folderRows.length === 0 ? (
              <p className="px-3 py-2 text-xs text-fg-muted">{t('noFolders')}</p>
            ) : (
              <nav className="fm-nav">
                {folderRows.map(({ folder: f, depth, hasChildren }) => {
                  const active = view.kind === 'folder' && view.id === f.id;
                  const isCollapsed = collapsed.has(f.id);
                  return (
                    <div key={f.id} className={cn('fm-folder', active && 'active')} style={{ paddingInlineStart: `${6 + depth * 14}px` }}>
                      {hasChildren ? (
                        <button type="button" className={cn('fm-chev', isCollapsed && 'collapsed')} onClick={() => toggle(f.id)} aria-label={tc(isCollapsed ? 'actions.expand' : 'actions.collapse', { defaultValue: isCollapsed ? 'Expand' : 'Collapse' })}>
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      ) : (
                        <span className="fm-chev fm-chev-spacer" aria-hidden />
                      )}
                      <button type="button" className="fm-folder-btn" onClick={() => setView({ kind: 'folder', id: f.id })}>
                        {active
                          ? <FolderOpen className="fm-folder-ico h-[18px] w-[18px] flex-none" aria-hidden />
                          : <FolderIcon className="fm-folder-ico h-[18px] w-[18px] flex-none" aria-hidden />}
                        <span className="truncate">{f.name}</span>
                        <span className="fm-count">{counts[`folder:${f.id}`] ?? 0}</span>
                      </button>
                      {canWrite && (
                        <span className="fm-folder-actions">
                          <button type="button" onClick={() => setFolderDialog({ mode: 'create', parentId: f.id })} title={t('newSubfolder')} aria-label={t('newSubfolder')}>
                            <FolderPlus className="h-3.5 w-3.5" aria-hidden />
                          </button>
                          <button type="button" onClick={() => setFolderDialog({ mode: 'rename', parentId: f.parentId, folder: f })} title={t('rename')} aria-label={t('rename')}>
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                          </button>
                          {canDelete && (
                            <button type="button" onClick={() => void onDeleteFolder(f)} title={t('delete')} aria-label={t('delete')}>
                              <Trash2 className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </nav>
            )}
          </div>
          <StorageMeter
            total={storage.total}
            buckets={storage.buckets}
            title={t('storage')}
            countLabel={t('filesCount', { count: storage.count })}
            catLabel={(key) => t(`views.${key}`, { defaultValue: key })}
          />
        </Card>

        {/* ---- main: toolbar + files ---- */}
        <div className="fm-main flex flex-col gap-3">
          <div className="fm-toolbar">
            <div className="fm-breadcrumb">
              <button type="button" className="fm-crumb" onClick={() => setView({ kind: 'smart', key: 'all' })}>{t('views.all')}</button>
              {view.kind === 'folder' &&
                folderPath(byId, view.id).map((f) => (
                  <span key={f.id} className="fm-crumb-seg">
                    <span className="fm-crumb-sep" aria-hidden>/</span>
                    <button type="button" className="fm-crumb" onClick={() => setView({ kind: 'folder', id: f.id })}>{f.name}</button>
                  </span>
                ))}
            </div>
            <label className="fm-search">
              <Search className="h-4 w-4 flex-none" aria-hidden />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('search')} aria-label={t('search')} />
            </label>
            <div className="fm-seg" role="group">
              <button type="button" aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')} title={t('gridView')} aria-label={t('gridView')}>
                <LayoutGrid className="h-4 w-4" aria-hidden />
              </button>
              <button type="button" aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')} title={t('listView')} aria-label={t('listView')}>
                <ListIcon className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>

          <Card
            className={cn('fm-drop p-0', dragOver && 'drag-over')}
            onDragOver={canWrite ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
            onDragLeave={canWrite ? () => setDragOver(false) : undefined}
            onDrop={canWrite ? (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) void doUpload(e.dataTransfer.files); } : undefined}
          >
            {dragOver && (
              <div className="fm-drop-overlay">
                <Upload className="h-7 w-7" aria-hidden />
                <span>{t('dropHint')}</span>
              </div>
            )}
            <div className="fm-main-head">
              <h2 className="text-base font-semibold">{heading}</h2>
              <span className="text-xs text-fg-muted">{t('filesCount', { count: total })}</span>
            </div>

            {loading ? (
              <div className="fm-grid p-4">
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
              </div>
            ) : visibleFiles.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  icon={<FilesIcon className="h-8 w-8" aria-hidden />}
                  title={query ? t('noMatches') : t('empty')}
                  description={query ? undefined : t('emptyHint')}
                  action={canWrite && !query ? (
                    <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4" aria-hidden />{t('upload')}
                    </Button>
                  ) : undefined}
                />
              </div>
            ) : viewMode === 'grid' ? (
              <div className="fm-grid p-4">
                {visibleFiles.map((f, i) => (
                  <FileCard
                    key={f.id}
                    file={f}
                    index={i}
                    canWrite={canWrite}
                    canDelete={canDelete}
                    onOpen={() => (isImageMime(f.mimeType) ? setPreviewFile(f) : void onDownload(f))}
                    onMove={() => setMoveTarget(f)}
                    onDownload={() => void onDownload(f)}
                    onDelete={() => void onDeleteFile(f)}
                    sourceLabel={t(`source.${f.entityType}`, { defaultValue: f.entityType })}
                    linkedLabel={t('linked')}
                    moveLabel={t('move')}
                    downloadLabel={t('download')}
                    deleteLabel={t('delete')}
                  />
                ))}
              </div>
            ) : (
              <div className="fm-list-wrap">
                <table className="fm-list">
                  <thead>
                    <tr>
                      <th>{t('columns.name')}</th>
                      <th>{t('columns.source')}</th>
                      <th className="fm-num">{t('columns.size')}</th>
                      <th>{t('columns.added')}</th>
                      <th aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFiles.map((f) => (
                      <tr key={f.id}>
                        <td>
                          <button type="button" className="fm-list-name" onClick={() => (isImageMime(f.mimeType) ? setPreviewFile(f) : void onDownload(f))}>
                            <FileThumb file={f} size={28} />
                            <span className="truncate">{f.filename}</span>
                          </button>
                        </td>
                        <td><Badge tone={f.entityType === 'file' ? 'neutral' : 'info'}>{t(`source.${f.entityType}`, { defaultValue: f.entityType })}</Badge></td>
                        <td className="fm-num">{formatBytes(f.sizeBytes)}</td>
                        <td className="text-fg-muted">{formatDate(f.uploadedAt, { locale: i18n.language })}</td>
                        <td>
                          <div className="fm-row-actions">
                            {canWrite && <button type="button" onClick={() => setMoveTarget(f)} title={t('move')} aria-label={t('move')}><Move className="h-4 w-4" aria-hidden /></button>}
                            <button type="button" onClick={() => void onDownload(f)} title={t('download')} aria-label={t('download')}><Download className="h-4 w-4" aria-hidden /></button>
                            {canDelete && <button type="button" onClick={() => void onDeleteFile(f)} title={t('delete')} aria-label={t('delete')}><Trash2 className="h-4 w-4 text-danger" aria-hidden /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && visibleFiles.length > 0 && visibleFiles.length < total && (
              <div className="fm-more">
                <Button variant="secondary" size="sm" onClick={() => void loadMore()} loading={loadingMore}>
                  {t('loadMore')}
                </Button>
                <span className="text-xs text-fg-muted">{t('shownOf', { shown: visibleFiles.length, total })}</span>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ---- folder name dialog ---- */}
      <FolderDialog
        state={folderDialog}
        onClose={() => setFolderDialog(null)}
        onSave={onSaveFolder}
        labels={{
          create: t('createFolder'),
          rename: t('renameFolder'),
          name: t('folderName'),
          save: tc('actions.save'),
          cancel: tc('actions.cancel'),
        }}
      />

      {/* ---- move dialog ---- */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('move')}</DialogTitle>
            <DialogDescription className="truncate">{moveTarget?.filename}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1">
            <button type="button" className={cn('fm-move-opt', moveTarget?.folderId === null && 'active')} onClick={() => void onMove(null)}>
              <FolderInput className="h-4 w-4" aria-hidden />{t('unfiledOption')}
            </button>
            {(folders ?? []).length > 0 && <div className="my-1 border-t border-border" />}
            {flattenFolders(byParent, '', new Set()).map(({ folder: f, depth }) => (
              <button key={f.id} type="button" className={cn('fm-move-opt', moveTarget?.folderId === f.id && 'active')} style={{ paddingInlineStart: `${12 + depth * 16}px` }} onClick={() => void onMove(f.id)}>
                <FolderIcon className="fm-folder-ico h-4 w-4" aria-hidden />{f.name}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- image preview ---- */}
      <Dialog open={!!previewFile} onOpenChange={(o) => !o && setPreviewFile(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate">{previewFile?.filename}</DialogTitle>
            <DialogDescription>
              {previewFile && `${formatBytes(previewFile.sizeBytes)} · ${formatDate(previewFile.uploadedAt, { locale: i18n.language })}`}
            </DialogDescription>
          </DialogHeader>
          {previewFile && <PreviewImage file={previewFile} />}
          <DialogFooter>
            {previewFile && <Button variant="secondary" onClick={() => void onDownload(previewFile)}><Download className="h-4 w-4" aria-hidden />{t('download')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface FileCardProps {
  file: FileItem;
  index: number;
  canWrite: boolean;
  canDelete: boolean;
  onOpen: () => void;
  onMove: () => void;
  onDownload: () => void;
  onDelete: () => void;
  sourceLabel: string;
  linkedLabel: string;
  moveLabel: string;
  downloadLabel: string;
  deleteLabel: string;
}

function FileCard({ file, index, canWrite, canDelete, onOpen, onMove, onDownload, onDelete, sourceLabel, moveLabel, downloadLabel, deleteLabel }: FileCardProps): JSX.Element {
  const linked = file.entityType !== 'file';
  // Staggered entrance — cap the delay so a large grid doesn't crawl in.
  const delay = `${Math.min(index, 14) * 30}ms`;
  return (
    <div className="fm-card" style={{ animationDelay: delay }}>
      <button type="button" className="fm-card-preview" onClick={onOpen} title={file.filename}>
        <FileThumb file={file} size={120} />
        <Badge tone={linked ? 'info' : 'neutral'} className="fm-card-badge">{sourceLabel}</Badge>
      </button>
      <div className="fm-card-meta">
        <div className="truncate text-sm font-medium text-fg" title={file.filename}>{file.filename}</div>
        <div className="text-xs text-fg-muted">{formatBytes(file.sizeBytes)}</div>
      </div>
      <div className="fm-card-actions">
        {canWrite && <button type="button" onClick={onMove} title={moveLabel} aria-label={moveLabel}><Move className="h-4 w-4" aria-hidden /></button>}
        <button type="button" onClick={onDownload} title={downloadLabel} aria-label={downloadLabel}><Download className="h-4 w-4" aria-hidden /></button>
        {canDelete && <button type="button" onClick={onDelete} title={deleteLabel} aria-label={deleteLabel}><Trash2 className="h-4 w-4 text-danger" aria-hidden /></button>}
      </div>
    </div>
  );
}

/**
 * Image thumbnail — fetched as an auth object URL; typed doc icon otherwise.
 *
 * The fetch waits until the tile is on (or just off) screen. Attachments have
 * no server-side thumbnails, so each one costs its full size — the shop's
 * tenant holds 3,900 images, and rendering them all eagerly downloaded a
 * quarter of a gigabyte before the first row was usable.
 */
function FileThumb({ file, size }: { file: FileItem; size: number }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const holder = useRef<HTMLDivElement | null>(null);
  const isImg = isImageMime(file.mimeType);

  useEffect(() => {
    if (!isImg || visible) return undefined;
    const el = holder.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } },
      { rootMargin: '400px' }, // start a screen early so scrolling stays smooth
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isImg, visible]);

  useEffect(() => {
    if (!isImg || !visible) return undefined;
    let active = true;
    let made: string | null = null;
    void fetchAttachmentObjectUrl(file.id)
      .then((u) => { if (active) { made = u; setUrl(u); } else URL.revokeObjectURL(u); })
      .catch(() => undefined);
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [file.id, isImg, visible]);

  const { Icon, cls } = docMeta(file.mimeType);
  return (
    <div ref={holder} className={cn('fm-thumb', !isImg && `fm-doc ${cls}`)} style={{ width: size, height: size }}>
      {isImg ? (
        <img src={url ?? undefined} alt="" className={cn('fm-thumb-img h-full w-full object-cover', url && 'loaded')} />
      ) : (
        <Icon style={{ width: size * 0.42, height: size * 0.42 }} aria-hidden />
      )}
    </div>
  );
}

interface StorageMeterProps {
  total: number;
  buckets: { key: string; bytes: number }[];
  title: string;
  countLabel: string;
  catLabel: (key: string) => string;
}

/** Drive-style storage footer: total used + a stacked usage bar by file type. */
function StorageMeter({ total, buckets, title, countLabel, catLabel }: StorageMeterProps): JSX.Element {
  return (
    <div className="fm-storage">
      <div className="fm-storage-head">
        <HardDrive className="h-4 w-4 flex-none text-fg-muted" aria-hidden />
        <span className="fm-storage-title">{title}</span>
        <span className="fm-storage-total">{formatBytes(total)}</span>
      </div>
      <div className="fm-storage-bar" aria-hidden>
        {total === 0
          ? <span className="fm-storage-empty" />
          : buckets.map((b) => (
              <span
                key={b.key}
                className={`fm-seg-fill seg-${b.key}`}
                style={{ width: `${Math.max((b.bytes / total) * 100, 2)}%` }}
                title={`${catLabel(b.key)} · ${formatBytes(b.bytes)}`}
              />
            ))}
      </div>
      <div className="fm-storage-sub">{countLabel}</div>
      {buckets.length > 0 && (
        <ul className="fm-storage-legend">
          {buckets.map((b) => (
            <li key={b.key}>
              <span className={`fm-dot seg-${b.key}`} aria-hidden />
              <span className="truncate">{catLabel(b.key)}</span>
              <span className="fm-legend-size">{formatBytes(b.bytes)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PreviewImage({ file }: { file: FileItem }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let made: string | null = null;
    void fetchAttachmentObjectUrl(file.id)
      .then((u) => { if (active) { made = u; setUrl(u); } else URL.revokeObjectURL(u); })
      .catch(() => undefined);
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [file.id]);
  return (
    <div className="grid max-h-[60vh] place-items-center overflow-hidden rounded-lg border border-border bg-bg-subtle">
      {url ? <img src={url} alt={file.filename} className="max-h-[60vh] w-auto object-contain" /> : <Loader2 className="m-12 h-6 w-6 animate-spin text-fg-muted" aria-hidden />}
    </div>
  );
}

interface FolderDialogProps {
  state: { mode: 'create' | 'rename'; parentId: string | null; folder?: Folder } | null;
  onClose: () => void;
  onSave: (name: string) => void | Promise<void>;
  labels: { create: string; rename: string; name: string; save: string; cancel: string };
}

function FolderDialog({ state, onClose, onSave, labels }: FolderDialogProps): JSX.Element {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(state?.folder?.name ?? ''); }, [state]);
  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try { await onSave(trimmed); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{state?.mode === 'rename' ? labels.rename : labels.create}</DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={labels.name} aria-label={labels.name} />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>{labels.cancel}</Button>
            <Button type="submit" loading={saving} disabled={!name.trim()}>{labels.save}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const FM_CSS = `
.fm-layout { display: grid; grid-template-columns: 260px 1fr; gap: 16px; align-items: start; }
@media (max-width: 860px) { .fm-layout { grid-template-columns: 1fr; } }
.fm-side { position: sticky; top: 0; display: flex; flex-direction: column; max-height: calc(var(--vh100) - 9.5rem); }
.fm-side-scroll { flex: 1; min-height: 0; overflow: auto; padding: 8px; }
.fm-cap { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px 4px; font-size: var(--fs-over); font-weight: 700; letter-spacing: var(--tracking-over); text-transform: uppercase; color: var(--text-subtle); }
[dir='rtl'] .fm-cap { letter-spacing: 0; }
.fm-cap-folders { margin-top: 6px; border-top: 1px solid var(--border-soft); padding-top: 10px; }
.fm-cap-add { display: grid; place-items: center; width: 22px; height: 22px; border-radius: 6px; border: 0; background: transparent; color: var(--text-subtle); cursor: pointer; }
.fm-cap-add:hover { background: var(--surface-2); color: var(--text); }
.fm-nav { display: flex; flex-direction: column; gap: 1px; }
.fm-nav-item { position: relative; display: flex; align-items: center; gap: 9px; width: 100%; padding: 7px 10px; border: 0; background: transparent; color: var(--text); border-radius: var(--r-md); cursor: pointer; font: inherit; font-size: var(--fs-sm); text-align: start; transition: background .15s, color .15s, padding-inline-start .15s; }
.fm-nav-item:hover { background: var(--surface-2); padding-inline-start: 13px; }
.fm-nav-item.active { background: color-mix(in oklab, var(--primary) 14%, transparent); color: var(--primary); font-weight: 600; }
.fm-nav-item svg { transition: transform .15s; }
.fm-nav-item:hover svg, .fm-nav-item.active svg { transform: scale(1.1); }
.fm-count { margin-inline-start: auto; font-size: 11px; color: var(--text-subtle); font-family: var(--font-num); background: var(--surface-2); border-radius: var(--r-pill); padding: 1px 7px; transition: background .15s, color .15s; }
.fm-nav-item.active .fm-count { color: var(--primary); background: color-mix(in oklab, var(--primary) 20%, transparent); }
.fm-nav-item.active .fm-count { color: var(--primary); }
.fm-folder { position: relative; display: flex; align-items: center; gap: 2px; border-radius: var(--r-md); padding-inline-end: 4px; transition: background .15s; }
.fm-folder:hover { background: var(--surface-2); }
.fm-folder.active { background: color-mix(in oklab, var(--primary) 14%, transparent); }
/* coloured smart-view icons (shared palette with the storage meter) */
.fm-svico { display: inline-flex; flex: none; color: var(--text-subtle); }
.fm-svico.tone-images { color: var(--primary); }
.fm-svico.tone-categories { color: var(--info); }
.fm-svico.tone-branding { color: var(--warning); }
.fm-svico.tone-pages { color: color-mix(in oklab, var(--primary) 45%, var(--danger)); }
.fm-svico.tone-documents { color: var(--success); }
.fm-svico.tone-uploads { color: var(--text-subtle); }
.fm-folder-ico { color: color-mix(in oklab, var(--warning) 88%, var(--text)); }
/* active accent bar */
.fm-nav-item.active::before, .fm-folder.active::before { content: ''; position: absolute; inset-inline-start: 0; top: 50%; height: 56%; width: 3px; transform: translateY(-50%); border-radius: 0 3px 3px 0; background: var(--primary); }
.fm-nav-item.active:dir(rtl)::before, .fm-folder.active:dir(rtl)::before { border-radius: 3px 0 0 3px; }
.fm-chev { display: grid; place-items: center; width: 18px; height: 26px; flex: none; border: 0; background: transparent; color: var(--text-subtle); cursor: pointer; transition: transform .15s; }
.fm-chev.collapsed { transform: rotate(-90deg); }
.fm-chev.collapsed:dir(rtl) { transform: rotate(90deg); }
.fm-chev-spacer { visibility: hidden; cursor: default; }
.fm-folder-btn { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; padding: 6px 2px; border: 0; background: transparent; color: var(--text); cursor: pointer; font: inherit; font-size: var(--fs-sm); text-align: start; }
.fm-folder.active .fm-folder-btn { color: var(--primary); font-weight: 600; }
.fm-folder-actions { display: inline-flex; gap: 1px; opacity: 0; transition: opacity .12s; }
.fm-folder:hover .fm-folder-actions, .fm-folder:focus-within .fm-folder-actions { opacity: 1; }
.fm-folder-actions button { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 6px; border: 0; background: transparent; color: var(--text-subtle); cursor: pointer; }
.fm-folder-actions button:hover { background: var(--surface); color: var(--text); }
.fm-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fm-breadcrumb { display: flex; align-items: center; gap: 4px; min-width: 0; flex: 1; font-size: var(--fs-sm); }
.fm-crumb { border: 0; background: transparent; color: var(--text-subtle); cursor: pointer; font: inherit; padding: 2px 4px; border-radius: 6px; }
.fm-crumb:hover { color: var(--text); background: var(--surface-2); }
.fm-crumb-seg { display: inline-flex; align-items: center; gap: 4px; }
.fm-crumb-sep { color: var(--text-muted); }
.fm-search { display: inline-flex; align-items: center; gap: 8px; height: 36px; width: min(280px, 100%); padding: 0 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-md); color: var(--text-subtle); }
.fm-search input { border: 0; background: transparent; outline: none; color: var(--text); font: inherit; font-size: var(--fs-sm); flex: 1; min-width: 0; }
.fm-seg { display: inline-flex; border: 1px solid var(--border); border-radius: var(--r-md); overflow: hidden; }
.fm-seg button { display: grid; place-items: center; width: 36px; height: 36px; border: 0; background: var(--surface); color: var(--text-subtle); cursor: pointer; }
.fm-seg button + button { border-inline-start: 1px solid var(--border); }
.fm-seg button[aria-pressed='true'] { background: color-mix(in oklab, var(--primary) 16%, var(--surface)); color: var(--primary); }
.fm-drop { position: relative; }
.fm-drop.drag-over { outline: 2px dashed var(--primary); outline-offset: -4px; }
.fm-drop-overlay { position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; background: color-mix(in oklab, var(--primary) 8%, var(--surface)); color: var(--primary); font-weight: 600; pointer-events: none; border-radius: inherit; }
.fm-main-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 16px; border-bottom: 1px solid var(--border-soft); }
.fm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; }
.fm-more { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 4px 16px 16px; }
.fm-card { display: flex; flex-direction: column; border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; background: var(--surface); transition: border-color .18s, box-shadow .18s, transform .18s; animation: fmFadeUp .34s cubic-bezier(.22,.61,.36,1) both; will-change: transform, opacity; }
.fm-card:hover { border-color: color-mix(in oklab, var(--primary) 45%, var(--border)); box-shadow: 0 10px 24px -12px color-mix(in oklab, var(--primary) 45%, transparent); transform: translateY(-3px); }
.fm-card-preview { position: relative; display: grid; place-items: center; aspect-ratio: 1 / 1; border: 0; background: var(--surface-2); cursor: pointer; padding: 0; overflow: hidden; }
.fm-card-preview .fm-thumb { width: 100% !important; height: 100% !important; border: 0; border-radius: 0; background: transparent; }
.fm-card-preview .fm-thumb-img { transition: transform .35s cubic-bezier(.22,.61,.36,1); }
.fm-card:hover .fm-card-preview .fm-thumb-img { transform: scale(1.06); }
.fm-card-badge { position: absolute; top: 6px; inset-inline-start: 6px; box-shadow: 0 1px 3px rgb(0 0 0 / .18); backdrop-filter: blur(4px); }
.fm-card-meta { padding: 8px 10px; min-width: 0; }
.fm-card-actions { display: flex; gap: 2px; padding: 0 8px 8px; opacity: 0; transition: opacity .12s; }
.fm-card:hover .fm-card-actions, .fm-card:focus-within .fm-card-actions { opacity: 1; }
.fm-card-actions button { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 7px; border: 0; background: var(--surface-2); color: var(--text-subtle); cursor: pointer; }
.fm-card-actions button:hover { background: var(--surface); color: var(--text); }
.fm-thumb { display: grid; place-items: center; flex: none; overflow: hidden; border-radius: 8px; border: 1px solid var(--border); background: var(--surface-2); }
.fm-thumb-img { opacity: 0; transition: opacity .3s ease; }
.fm-thumb-img.loaded { opacity: 1; }
.fm-doc.doc-pdf { color: var(--danger); background: color-mix(in oklab, var(--danger) 10%, var(--surface-2)); }
.fm-doc.doc-sheet { color: var(--success); background: color-mix(in oklab, var(--success) 10%, var(--surface-2)); }
.fm-doc.doc-word { color: var(--info); background: color-mix(in oklab, var(--info) 10%, var(--surface-2)); }
.fm-doc.doc-generic { color: var(--text-subtle); }
.fm-list-wrap { overflow: auto; }
table.fm-list { width: 100%; border-collapse: collapse; }
.fm-list thead th { position: sticky; top: 0; background: var(--surface); text-align: start; font-size: var(--fs-over); font-weight: 700; letter-spacing: var(--tracking-over); text-transform: uppercase; color: var(--text-subtle); padding: 10px 16px; border-bottom: 1px solid var(--border); white-space: nowrap; }
[dir='rtl'] .fm-list thead th { letter-spacing: 0; }
.fm-list td { padding: 8px 16px; border-bottom: 1px solid var(--border-soft); font-size: var(--fs-sm); color: var(--text); vertical-align: middle; }
.fm-list .fm-num { text-align: end; font-family: var(--font-num); color: var(--text-subtle); }
.fm-list-name { display: inline-flex; align-items: center; gap: 10px; border: 0; background: transparent; color: var(--text); cursor: pointer; font: inherit; font-size: var(--fs-sm); min-width: 0; max-width: 360px; }
.fm-list-name:hover { color: var(--primary); }
.fm-row-actions { display: inline-flex; gap: 1px; justify-content: flex-end; }
.fm-row-actions button { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 6px; border: 0; background: transparent; color: var(--text-subtle); cursor: pointer; }
.fm-row-actions button:hover { background: var(--surface-2); color: var(--text); }
.fm-move-opt { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 12px; border: 0; background: transparent; color: var(--text); border-radius: var(--r-md); cursor: pointer; font: inherit; font-size: var(--fs-sm); text-align: start; transition: background .12s, padding-inline-start .12s; }
.fm-move-opt:hover { background: var(--surface-2); padding-inline-start: 15px; }
.fm-move-opt.active { background: color-mix(in oklab, var(--primary) 14%, transparent); color: var(--primary); font-weight: 600; }

/* ---- storage meter (Drive-style usage footer) ---- */
.fm-storage { border-top: 1px solid var(--border-soft); padding: 12px 14px 14px; background: color-mix(in oklab, var(--surface-2) 40%, var(--surface)); }
.fm-storage-head { display: flex; align-items: center; gap: 7px; }
.fm-storage-title { font-size: var(--fs-sm); font-weight: 600; color: var(--text); }
.fm-storage-total { margin-inline-start: auto; font-size: var(--fs-sm); font-weight: 700; color: var(--text); font-family: var(--font-num); }
.fm-storage-bar { display: flex; height: 9px; margin-top: 9px; border-radius: var(--r-pill); overflow: hidden; background: var(--surface-2); box-shadow: inset 0 0 0 1px var(--border-soft); }
.fm-seg-fill { height: 100%; display: block; transition: width .5s cubic-bezier(.22,.61,.36,1); animation: fmBarGrow .6s ease both; }
.fm-seg-fill + .fm-seg-fill { box-shadow: -1px 0 0 0 color-mix(in oklab, var(--surface) 70%, transparent); }
.fm-storage-empty { height: 100%; width: 100%; }
.fm-storage-sub { margin-top: 7px; font-size: 11px; color: var(--text-subtle); }
.fm-storage-legend { display: flex; flex-direction: column; gap: 4px; margin-top: 9px; }
.fm-storage-legend li { display: flex; align-items: center; gap: 7px; font-size: 11px; color: var(--text-subtle); }
.fm-dot { width: 8px; height: 8px; flex: none; border-radius: 50%; }
.fm-legend-size { margin-inline-start: auto; font-family: var(--font-num); color: var(--text-subtle); }
.seg-images { background: var(--primary); }
.seg-categories { background: var(--info); }
.seg-branding { background: var(--warning); }
.seg-pages { background: color-mix(in oklab, var(--primary) 45%, var(--danger)); }
.seg-documents { background: var(--success); }
.seg-uploads { background: var(--text-subtle); }
.seg-other { background: var(--text-muted); }

@keyframes fmFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes fmBarGrow { from { transform: scaleX(0); transform-origin: inline-start; } to { transform: scaleX(1); } }
@media (prefers-reduced-motion: reduce) {
  .fm-card, .fm-seg-fill { animation: none !important; }
  .fm-card, .fm-card:hover, .fm-thumb-img, .fm-card .fm-thumb-img, .fm-nav-item, .fm-nav-item:hover, .fm-move-opt:hover { transition: none !important; transform: none !important; }
}
`;
